import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { Screen } from "@/components/Screen";
import { Button } from "@/components/ui/button";
import {
  Image as ImageIcon,
  RefreshCw,
  Video,
  AlertTriangle,
  CalendarRange,
  Hand,
  ChevronRight,
} from "lucide-react";
import { StatusChip } from "@/components/roava";
import { useUploadActions, type UploadProgressItem } from "@/lib/upload/use-upload-actions";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_authenticated/upload")({
  head: () =>
    pageMeta({
      title: "Upload Media — Roava by WGP Apps",
      description: "Add your photos and videos to the shared trip library.",
      path: "/upload",
    }),
  component: UploadPage,
});

function UploadPage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const {
    mode,
    wifiOnly,
    conn,
    tripStartDate,
    tripEndDate,
    pickFromPhotos,
    scanTripMedia,
    recordVideo,
    progressItems,
    webFileInput,
    reviewSheet,
    gateDialogs,
  } = useUploadActions();

  const startDate = tripStartDate ?? undefined;
  const endDate = tripEndDate ?? undefined;
  const hasDates = !!(startDate && endDate);
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };
  const rangeLabel = hasDates ? `${fmt(startDate!)} – ${fmt(endDate!)}` : t("upload.range_not_set");
  const isAuto = mode === "auto";

  const ChangeLink = (
    <button
      type="button"
      onClick={() => nav({ to: "/upload-mode" })}
      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
    >
      {t("upload.change_settings")}
      <ChevronRight className="size-3" />
    </button>
  );

  const cellularBanner = !wifiOnly && conn === "cellular" && (
    <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
      <AlertTriangle className="size-4 shrink-0 mt-0.5" />
      <p>{t("upload.cellular_warn")}</p>
    </div>
  );

  const ManualButtons = (
    <div className="grid gap-2">
      <Button
        onClick={() => void pickFromPhotos()}
        variant="secondary"
        className="w-full h-12 rounded-2xl font-semibold"
      >
        <ImageIcon className="size-4 mr-2" />
        {t("upload.pick_photos")}
      </Button>
      <Button
        onClick={() => void recordVideo()}
        variant="outline"
        className="w-full h-12 rounded-2xl font-semibold"
      >
        <Video className="size-4 mr-2" />
        {t("upload.record_video")}
      </Button>
    </div>
  );

  const ReviewSection = (
    <Button
      onClick={() => void scanTripMedia()}
      className="w-full h-12 rounded-2xl font-semibold"
    >
      <RefreshCw className="size-4 mr-2" />
      {t("upload.review_media")}
    </Button>
  );

  return (
    <>
      <Screen title={t("upload.title")} subtitle={t("upload.subtitle")}>
        {webFileInput}

        {/* Read-only current-mode status card */}
        <section className="rounded-2xl border border-border bg-card p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="size-10 shrink-0 rounded-xl grid place-items-center bg-accent text-accent-foreground">
              {isAuto ? <CalendarRange className="size-5" /> : <Hand className="size-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                {t("upload.current_mode")}
              </p>
              <h2 className="font-semibold mt-0.5">
                {isAuto ? t("upload.auto_selected") : t("upload.manual_selected")}
              </h2>
              {isAuto && (
                <p className="text-sm text-muted-foreground mt-1">
                  {t("upload.date_range_label")}{" "}
                  <span className="font-medium text-foreground">{rangeLabel}</span>
                </p>
              )}
              {ChangeLink}
            </div>
          </div>
        </section>

        {isAuto ? (
          <>
            <section className="rounded-2xl border border-border bg-card p-4 mb-4 space-y-3">
              {cellularBanner}
              {ReviewSection}
            </section>
            <section className="rounded-2xl border border-border bg-card p-4 mb-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold">{t("upload.manual_actions_title")}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("upload.manual_no_range")}
                </p>
              </div>
              {ManualButtons}
            </section>
          </>
        ) : (
          <>
            <section className="rounded-2xl border border-border bg-card p-4 mb-4 space-y-3">
              {cellularBanner}
              {ManualButtons}
            </section>
            <section className="rounded-2xl border border-border bg-card p-4 mb-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold">{t("upload.review_media")}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("upload.review_uses_range")}{" "}
                  <span className="font-medium text-foreground">{rangeLabel}</span>
                </p>
              </div>
              {ReviewSection}
            </section>
          </>
        )}

        {progressItems.length > 0 && (
          <div className="mt-6 grid gap-2">
            {progressItems.slice(0, 50).map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-3 overflow-hidden rounded-xl border border-border bg-card p-3 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{it.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("upload.size_mb", { n: (it.size / 1024 / 1024).toFixed(1) })}
                  </p>
                </div>
                <div className="shrink-0">
                  <StatusBadge status={it.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Screen>
      {reviewSheet}
      {gateDialogs}
    </>
  );
}

function StatusBadge({ status }: { status: UploadProgressItem["status"] }) {
  const { t } = useTranslation();
  const map = {
    queued: { variant: "neutral" as const, label: t("upload.status_queued") },
    uploading: { variant: "info" as const, label: t("upload.status_uploading") },
    done: { variant: "success" as const, label: t("upload.status_done") },
    failed: { variant: "danger" as const, label: t("upload.status_failed") },
  };
  const { variant, label } = map[status];
  return <StatusChip variant={variant}>{label}</StatusChip>;
}
